import React from 'react';
import { to } from 'acl';
import { Link, useParams } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { Button, TabConfig } from '@odin-labs/components';
import { AuthContext } from 'auth';
import { AlertService } from 'components/alertService';
import { LoadingError } from 'components/loadingError';
import { BanReasonModal } from 'components/modals/BanReasonModal';
import { MutationUpdateJobsiteWorkerArgs } from 'apollo/generated/client-operations';
import { UPDATE_JOBSITE_WORKER } from 'containers/workerOnboardings/helpers/queries';
import { getGraphQLError } from 'utils/error';
import {
  JobsiteBan,
  WorkerProfileBanner,
} from 'containers/worker/helpers/WorkerProfileBanner';

import {
  DateRangeFilterValues,
  ensureDistinctItems,
  ensureNonEmptyItems,
  getFullNameForUser,
  useBoolean,
  useModalState,
} from 'utils';
import { useLegacyState } from 'utils/useLegacyState';
import { convertUTCtoLocalDate } from 'utils/dates';

import { AuthUser } from 'auth/types';
import { Grid2Icon, LockKeyholeIcon, PrintIcon } from 'components/icons';
import { Container } from 'components/container';
import { NewHeader } from 'components/header/NewHeader';
import { RoutedTabsPages, useRoutedTabs } from 'components/tabs';
import { HeaderAction } from 'components/header/types';
import { useFlags } from 'featureFlags';
import { JobsiteWorker, WorkerTabApi, WorkerTabProps } from './types';
import { AssignWorkerModal, ChangeWorkerAccessModal } from './modals';
import {
  WorkerOverview,
  WorkerAssignments,
  WorkerDocuments,
  WorkerMedical,
  WorkerBadges,
  WorkerAccessActivity,
  WorkerTimecards,
  WorkerComments,
  WorkerAdminActions,
  WorkerConnections,
  WorkerUserRoles,
  WorkerFormSubmissions,
} from './tabs';
import { useWorkerData } from './data/useWorkerData';
import { WorkerTitle } from './components';
import { WorkerAccessGroups } from './tabs/workerAccessGroups/WorkerAccessGroups';

const getTabsConfig = (
  user: AuthUser,
  connectionsEnabled: boolean,
): TabConfig<WorkerTabProps>[] =>
  ensureNonEmptyItems<TabConfig<WorkerTabProps>>([
    user.isAllowed(to.seeWorkerProfileOverview) && {
      name: `overview`,
      text: 'Overview',
      relativePath: '',
      component: WorkerOverview,
    },
    user.isAllowed(to.seeWorkerProfileJobsites) && {
      name: 'jobsites',
      text: 'Jobsites',
      relativePath: '/jobsites',
      component: WorkerAssignments,
    },
    user.isAllowed(to.seeWorkerProfileDocuments) && {
      name: `documents`,
      text: 'Documents',
      relativePath: '/documents',
      component: WorkerDocuments,
    },
    user.isAllowed(to.seeWorkerProfileMedical) && {
      name: `medical`,
      text: 'Medical',
      relativePath: '/medical',
      component: WorkerMedical,
    },
    user.isAllowed(to.seeWorkerProfileBadges) && {
      name: `badges`,
      text: 'Badges',
      relativePath: '/badges',
      leftIcon: user.hasPaidAccess ? undefined : LockKeyholeIcon,
      component: WorkerBadges,
    },
    user.isAllowed(to.seeWorkerProfileAccessActivity) && {
      name: `accessActivity`,
      text: 'Access Activity',
      relativePath: '/access-activity',
      leftIcon: user.hasPaidAccess ? undefined : LockKeyholeIcon,
      component: WorkerAccessActivity,
    },
    user.isAllowed(to.seeWorkerProfileTimecards) && {
      name: `timeCards`,
      text: 'Timecards',
      relativePath: '/timecards',
      leftIcon: user.hasPaidAccess ? undefined : LockKeyholeIcon,
      component: WorkerTimecards,
    },
    user.isAllowed(to.seeWorkerProfileFormSubmissions) && {
      name: `formSubmissions`,
      text: 'Logs',
      relativePath: '/forms',
      leftIcon: user.hasPaidAccess ? undefined : LockKeyholeIcon,
      component: WorkerFormSubmissions,
    },
    user.isAllowed(to.seeWorkerProfileComments) && {
      name: `comments`,
      text: 'Comments',
      relativePath: '/comments',
      component: WorkerComments,
    },
    user.isAllowed(to.seeWorkerProfileAccessGroups) && {
      name: 'accessGroups',
      text: 'Access groups',
      relativePath: '/access-groups',
      component: WorkerAccessGroups,
    },
    connectionsEnabled &&
      user.isAllowed(to.seeWorkerProfileActions) && {
        name: `connections`,
        text: 'Connections',
        relativePath: '/connections',
        component: WorkerConnections,
      },
    user.isAllowed(to.seeWorkerProfileActions) && {
      name: `actions`,
      text: 'Actions',
      relativePath: '/actions',
      component: WorkerAdminActions,
    },
    user.isAllowed(to.seeWorkerProfileUserRoles) && {
      name: `userRoles`,
      text: 'User Roles',
      relativePath: '/user-roles',
      component: WorkerUserRoles,
    },
  ]);

export function WorkerContainer(): React.ReactElement {
  const { workerId } = useParams<{ workerId: string }>();
  const baseRoute = `/worker/${workerId}`;

  const { currentUser: user } = React.useContext(AuthContext);
  const { connectionsEnabled } = useFlags();

  const {
    value: isAssignWorkerModalOpen,
    setTrue: openAssignWorkerModal,
    setFalse: closeAssignWorkerModal,
  } = useBoolean(false);

  const {
    value: jobsiteWorkerToChangeAccessFor,
    isModalOpen: isChangeWorkerAccessModalOpen,
    openModal: openChangeWorkerAccessModal,
    closeModal: closeChangeWorkerAccessModal,
    resetModalValue: resetJobsiteWorkerToChangeAccessFor,
  } = useModalState<JobsiteWorker>(null);

  const {
    value: jobsiteBanToShowReasonFor,
    isModalOpen: isBanReasonModalOpen,
    openModal: openBanReasonModal,
    closeModal: closeBanReasonModal,
    resetModalValue: resetJobsiteBanToShowReasonFor,
  } = useModalState<JobsiteBan>(null);

  const {
    loading,
    error,
    worker,
    isLocked,
    jobsiteWorkers,
    jobsiteIds,
    developerIds,
    jobsiteNames,
    refetch,
    updateWorkerState,
  } = useWorkerData(workerId);

  const tabsConfig = getTabsConfig(user, connectionsEnabled);
  const { tabs, currentTab } = useRoutedTabs({ tabsConfig, baseRoute });

  const jobsiteDocumentConditionalPasses = ensureDistinctItems(
    jobsiteWorkers
      .filter((jw) => jw.documentsCompletedSkipExpiresAt)
      .map((jw) => convertUTCtoLocalDate(jw.documentsCompletedSkipExpiresAt)),
  );

  const jobsiteMedicalConditionalPasses = ensureDistinctItems(
    jobsiteWorkers
      .filter((jw) => jw.urineTestConditionalPassExpiresAt)
      .map((jw) => convertUTCtoLocalDate(jw.urineTestConditionalPassExpiresAt)),
  );

  const jobsiteBans = jobsiteWorkers
    .filter((jw) => jw.isBanned)
    .map((jobsiteWorker) => {
      return {
        jobsiteWorker,
        jobsiteName: jobsiteWorker.jobsiteContractor?.jobsite?.name,
        workerName: getFullNameForUser(worker.user),
      };
    });

  // Same empty update the card's "Sync Worker Access" item sends: it is the
  // server re-running syncAccess that does the work.
  const [updateJobsiteWorker] = useMutation<
    JobsiteWorker,
    MutationUpdateJobsiteWorkerArgs
  >(UPDATE_JOBSITE_WORKER, {
    onCompleted: () => {
      AlertService.alert('success', 'Success', 'Access control synced!');
      refetch();
    },
    onError: (syncError) => {
      AlertService.alert(
        'danger',
        'Something went wrong!',
        getGraphQLError(syncError),
      );
    },
  });

  const syncJobsiteWorkerAccess = (jobsiteWorkerId: string): void => {
    updateJobsiteWorker({
      variables: { jobsiteWorkerId, jobsiteWorkerInput: {} },
    });
  };

  const getWorkersHeaderActions = (): React.ReactElement => {
    const content = ensureNonEmptyItems([
      !isLocked && !!jobsiteWorkers?.length && (
        <Button
          key="print"
          theme="secondary"
          text="Print View"
          hideTextOnMobile
          icon={PrintIcon}
          as={Link}
          to={`${baseRoute}/print`}
        />
      ),
      !isLocked &&
        !worker?.isArchived &&
        !user.isContractor &&
        user.isAllowed(to.assignWorkers) && (
          <Button
            key="assign-worker"
            text="Assign Worker"
            hideTextOnMobile
            icon={Grid2Icon}
            onClick={openAssignWorkerModal}
          />
        ),
    ]);
    return content.length ? (
      <div className="odin-flex odin-gap-x-3">{content}</div>
    ) : null;
  };

  const [tabApi, setTabApi] = React.useState<WorkerTabApi>(null);

  // Date/time filter range shared across tabs for as long as this container
  // stays mounted. Tabs seed their filter defaults from here and write user
  // changes back via `setDateRange`. It intentionally lives in component state
  // (not the URL), so navigating away and back resets tabs to their own
  // defaults. `useLegacyState` merges updates so a tab only overwrites the keys
  // it manages.
  const [dateRange, setDateRange] = useLegacyState<DateRangeFilterValues>({});

  const tabsPageProps: WorkerTabProps = {
    loading,
    worker,
    isLocked,
    jobsiteWorkers,
    jobsiteIds,
    developerIds,
    jobsiteNames,
    openChangeWorkerAccessModal,
    refetchWorkerData: refetch,
    onTabApiChange: setTabApi,
    updateWorkerState,
    dateRange,
    setDateRange,
  };

  if (loading || error) {
    return <LoadingError loading={loading} error={error} />;
  }

  return (
    <Container className="worker-container">
      <WorkerProfileBanner
        worker={worker}
        jobsiteBans={jobsiteBans}
        jobsiteDocumentConditionalPasses={jobsiteDocumentConditionalPasses}
        jobsiteMedicalConditionalPasses={jobsiteMedicalConditionalPasses}
        openChangeWorkerAccessModal={openChangeWorkerAccessModal}
        openBanReasonModal={openBanReasonModal}
        syncJobsiteWorkerAccess={syncJobsiteWorkerAccess}
      />
      <NewHeader
        title={<WorkerTitle worker={worker} />}
        tabsProps={{ tabs, currentTab }}
        actionsProps={{
          onReloadPressed: tabApi?.refetchData,
          baseRoute,
          headerActions: ensureNonEmptyItems<HeaderAction>([
            'back',
            'reload',
            user.isAllowed(to.seeChanges) ? 'history' : null,
          ]),
          children: getWorkersHeaderActions(),
        }}
      />
      <RoutedTabsPages
        baseRoute={baseRoute}
        tabs={tabs}
        componentProps={tabsPageProps}
      />
      {worker?.isArchived ? null : (
        <AssignWorkerModal
          isOpen={isAssignWorkerModalOpen}
          closeModal={closeAssignWorkerModal}
          selectedWorker={worker}
          jobsiteWorkers={jobsiteWorkers}
        />
      )}
      <BanReasonModal
        isOpen={isBanReasonModalOpen}
        closeModal={closeBanReasonModal}
        onClosed={resetJobsiteBanToShowReasonFor}
        jobsiteBan={jobsiteBanToShowReasonFor}
      />
      <ChangeWorkerAccessModal
        isOpen={isChangeWorkerAccessModalOpen}
        closeModal={closeChangeWorkerAccessModal}
        onClosed={resetJobsiteWorkerToChangeAccessFor}
        worker={worker}
        jobsiteWorker={jobsiteWorkerToChangeAccessFor}
      />
    </Container>
  );
}
